import React, { useCallback, useContext, useEffect, useState } from "react";
import "./FormCluster.css";
import FormInput from "../Form/FormInput";
import { UserContext } from "../../CheckoutPage";
import FormDropdown from "../FormDropdown/FormDropdown";
import {
  getAddressFromPincodeAPI,
  sendOtpAPI, // Restored OTP API
  verifyOtpAPI, // Restored OTP API
  verifyOtpPFHAPI,
} from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";
import StateDropdown from "../StateDropdown/StateDropdown";
import ReactGA from "react-ga4";
import { trackData } from "../../../../Utils/attribution/CheckoutAttribution";

const FormCluster = () => {
  const userContext = useContext(UserContext);
  const userDetails = userContext?.userDetails ?? {
    name: "",
    email: "",
    school: "",
    schoolID: 0,
    grade: 0,
    mobileNumber: sessionStorage.getItem("mobileNumber") || "",
    address: "",
    flatNo: "",
    city: "",
    state: "",
    pincode: "",
  };
  const setUserDetails = userContext?.setUserDetails ?? (() => {});
  const isOtpVerified = userContext?.isOtpVerified ?? false;
  const setIsOtpVerified = userContext?.setIsOtpVerified ?? (() => {});
  const pfhVerified = userContext?.pfhVerified ?? false;
  const backendSchool = userContext?.backendSchool ?? {
    schoolID: 0,
    schoolName: "",
    linkActive: false,
    city: "",
    state: "",
    individual_registration: false,
    examDate: "",
    isInternational: false,
    version_no: "V1",
    pg: "razorpay",
    pincode: "560076",
  };
  const individualReg = userContext?.individualReg ?? false;
  const [name, setName] = useState(sessionStorage.getItem("name") || "");
  const [school, setSchool] = useState(
    individualReg ? "" : backendSchool.schoolName
  );
  const [grade, setGrade] = useState<number>(
    Number(sessionStorage.getItem("grade") || 0)
  );
  console.log("userDetails-1", userDetails.mobileNumber);
  const [pfh, setPfh] = useState(sessionStorage.getItem("pfh") || "");
  const [email, setEmail] = useState(sessionStorage.getItem("email") || "");
  const [address, setAddress] = useState(
    sessionStorage.getItem("address") || ""
  );
  const [flatNo, setFlatNo] = useState(sessionStorage.getItem("flatNo") || "");
  const [city, setCity] = useState(sessionStorage.getItem("city") || "");
  const [state, setState] = useState(sessionStorage.getItem("state") || "");
  const [pincode, setPincode] = useState<string>(
    sessionStorage.getItem("pincode") || "560076"
  );
  const [error, setError] = useState({
    name: "",
    schoolName: "",
    schoolCity: "",
    grade: "",
    mobileNumber: "",
    email: "",
    otp: "", // OTP error
    sendOtpError: "", // OTP sending error
    address: "",
    flatNo: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [isVisible, setIsVisible] = useState(false);

  // OTP-related state variables
  const [otp, setOtp] = useState("");
  const [isOtpFieldVisible, setIsOtpFieldVisible] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [isOtpValid, setIsOtpValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [trackedFields, setTrackedFields] = useState<Record<string, boolean>>(
    () => {
      const saved = sessionStorage.getItem("trackedFormFields");
      return saved
        ? JSON.parse(saved)
        : {
            mobile: false,
            name: false,
            school: false,
            grade: false,
            email: false,
            pincode: false,
            city: false,
            state: false,
            otp_sent: false, // Restored OTP tracking
            otp_verified: false, // Restored OTP tracking
          };
    }
  );

  useEffect(() => {
    sessionStorage.setItem("trackedFormFields", JSON.stringify(trackedFields));
  }, [trackedFields]);

  const trackFieldCompletion = useCallback(
    (fieldName: string, isValid: boolean) => {
      if (isValid && !trackedFields[fieldName]) {
        trackData(`5678_checkout_${fieldName}`);
        ReactGA.event({
          category: "Form_Completion",
          action: `5678_checkout_${fieldName}`,
          label: "Checkout_Form",
        });
        setTrackedFields((prev) => ({
          ...prev,
          [fieldName]: true,
        }));
      }
    },
    [trackedFields]
  );

  const fetchStateAndCity = useCallback(
    async (pincode: string) => {
      console.log("🌐 fetchStateAndCity called with pincode:", pincode);
      setLoading(true);
      try {
        const response = await getAddressFromPincodeAPI(pincode);
        console.log("🌐 API response:", response);
        if (response.data.state && response.data.city) {
          console.log("✅ Setting city and state:", {
            city: response.data.city,
            state: response.data.state,
          });
          setState(response.data.state);
          setCity(response.data.city);
          sessionStorage.setItem("state", response.data.state);
          sessionStorage.setItem("city", response.data.city);
          setUserDetails((prev) => ({
            ...prev,
            state: response.data.state,
            city: response.data.city,
          }));
          setError((prevError) => ({ ...prevError, pincode: "" }));
          if (response.data.state.length >= 2) {
            trackFieldCompletion("state", true);
          }
          if (response.data.city.length >= 2) {
            trackFieldCompletion("city", true);
          }
        } else {
          console.log("❌ No city/state data in API response");
        }
      } catch (error) {
        console.error("❌ Error fetching state and city:", error);
        setError((prevError) => ({
          ...prevError,
          pincode: "Failed to fetch state and city.",
        }));
      } finally {
        setLoading(false);
      }
    },
    [setUserDetails, trackFieldCompletion]
  );

  // Handle grade URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utmGrade = urlParams.get("grade");

    if (utmGrade) {
      const gradeNum = parseInt(utmGrade, 10);
      if (!isNaN(gradeNum) && gradeNum >= 5 && gradeNum <= 12) {
        // Only set if there's no existing grade in sessionStorage
        const existingGrade = sessionStorage.getItem("grade");
        if (!existingGrade || existingGrade === "0") {
          sessionStorage.setItem("grade", utmGrade);
          setGrade(gradeNum);
          setUserDetails((prev) => ({ ...prev, grade: gradeNum }));
          console.log(`Grade set from UTM parameter: ${utmGrade}`);
        }
      } else {
        console.warn(
          `Invalid grade parameter: ${utmGrade}. Must be between 5-12.`
        );
      }
    }
  }, [setUserDetails]);

  // Auto-fill address fields from schoolDetails when available
  useEffect(() => {
    console.log("🔍 Auto-fill useEffect triggered with:", {
      backendSchool: backendSchool,
      individualReg: individualReg,
      currentPincode: pincode,
      sessionPincode: sessionStorage.getItem("pincode"),
    });

    if (backendSchool && !individualReg) {
      // Only auto-fill for non-individual registrations (school-based)
      const schoolPincode = backendSchool.pincode;

      console.log("🏫 School-based registration detected:", {
        schoolPincode: schoolPincode,
        currentPincode: pincode,
        currentCity: city,
        currentState: state,
        condition1: !!schoolPincode,
        condition2: schoolPincode !== "560076",
        condition3:
          !pincode || pincode === "560076" || pincode === schoolPincode,
        condition4: !city || !state,
        allConditionsMet:
          schoolPincode &&
          schoolPincode !== "560076" &&
          (!pincode || pincode === "560076" || pincode === schoolPincode) &&
          (!city || !state),
      });

      // Check if we have pincode from school and current form needs auto-fill
      // Trigger if: pincode is empty/default OR pincode matches school but city/state are missing
      if (
        schoolPincode &&
        schoolPincode !== "560076" &&
        (!pincode || pincode === "560076" || pincode === schoolPincode) &&
        (!city || !state)
      ) {
        console.log(
          "✅ Auto-filling pincode from school details and fetching city/state:",
          {
            pincode: schoolPincode,
          }
        );

        // Set pincode first
        setPincode(schoolPincode);
        sessionStorage.setItem("pincode", schoolPincode);

        // Clear pincode errors
        setError((prevError) => ({
          ...prevError,
          pincode: "",
        }));

        // Track pincode field completion
        if (schoolPincode.length === 6) {
          trackFieldCompletion("pincode", true);
        }

        // Update userDetails context with pincode
        setUserDetails((prev) => ({
          ...prev,
          pincode: schoolPincode,
        }));

        // Fetch city and state from the pincode API
        fetchStateAndCity(schoolPincode);
      } else {
        console.log("❌ Auto-fill conditions not met");
      }
    } else {
      console.log(
        "❌ Not a school-based registration or backendSchool not available:",
        {
          hasBackendSchool: !!backendSchool,
          isIndividualReg: individualReg,
        }
      );
    }
  }, [
    backendSchool,
    individualReg,
    pincode,
    city,
    state,
    setUserDetails,
    trackFieldCompletion,
    fetchStateAndCity,
  ]);

  // OTP sending function
  const sendOtp = async () => {
    setIsLoading(true);
    try {
      const response = await sendOtpAPI(userDetails.mobileNumber); // Replace with actual API call
      if (response.status === "Success") {
        setIsOtpFieldVisible(true);
        setIsResendEnabled(false);
        setResendTimer(30);
        startResendTimer();
        setError((prevError) => ({
          ...prevError,
          sendOtpError: "",
        }));
        if (trackedFields.mobile) {
          trackData("5678_checkout_otp_sent");
          ReactGA.event({
            category: "Form_Completion",
            action: "5678_checkout_otp_sent",
            label: "Checkout_Form",
          });
          setTrackedFields((prev) => ({
            ...prev,
            otp_sent: true,
          }));
        }
      } else {
        setError((prevError) => ({
          ...prevError,
          sendOtpError: "Error sending OTP, Please try again",
        }));
      }
    } catch (err: any) {
      setError((prevError) => ({
        ...prevError,
        sendOtpError: "Error sending OTP, Please try again",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // OTP verification function
  const verifyOtp = async () => {
    setIsLoading(true);
    setError((prev) => ({ ...prev, otp: "" }));

    try {
      const response = await (pfh ? verifyOtpPFHAPI : verifyOtpAPI)(
        userDetails.mobileNumber,
        otp
      );

      if (response.status !== "Success") {
        setError((prev) => ({
          ...prev,
          otp: "Invalid OTP. Please try again.",
        }));
        return;
      } else {
        setIsOtpVerified(true);
        setIsOtpFieldVisible(false);
        if (trackedFields.otp_verified) {
          trackData("5678_checkout_otp_verified");
          ReactGA.event({
            category: "Form_Completion",
            action: "5678_checkout_otp_verified",
            label: "Checkout_Form",
          });
          setTrackedFields((prev) => ({
            ...prev,
            otp_verified: true,
          }));
        }
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setError((prev) => ({ ...prev, otp: "Invalid OTP. Please try again." }));
    } finally {
      setIsLoading(false);
    }
  };

  // OTP-related functions
  const handleOtp = () => {
    if (!isOtpFieldVisible) {
      sendOtp();
    } else if (isResendEnabled) {
      handleResendOtp();
    }
  };

  const startResendTimer = () => {
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsResendEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOtp = () => {
    if (isResendEnabled) {
      sendOtp();
    }
  };

  // Update userDetails in UserContext
  useEffect(() => {
    setUserDetails({
      ...userDetails,
      name,
      grade,
      mobileNumber: userDetails.mobileNumber,
      email,
      school,
      schoolID: backendSchool.schoolID,
      address: address || "",
      flatNo: flatNo || "",
      city: city || "",
      state: state || "",
      pincode: pincode || "",
    });
  }, [
    name,
    grade,
    email,
    school,
    individualReg,
    address,
    flatNo,
    city,
    state,
    pincode,
  ]);

  // Validation patterns
  const emailValidation = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
  const mobileValidation = /^[1-9][0-9]{9}$/;
  const dubaiMobileValidation =
    /^((\+971|971|0)?(50|51|52|54|55|56|58|4))\d{7}$/;
  const otpValidation = /^\d{4}$/; // OTP validation pattern

  // Form change handler
  const handleFormChange = (event: any) => {
    const { value, name } = event.target;

    let updatedError = { ...error };
    switch (name) {
      case "name":
        updatedError.name =
          value.length < 3 ? "Please enter a valid name." : "";
        setName(value);
        sessionStorage.setItem("name", value);
        setUserDetails({
          ...userDetails,
          name: value,
          address: address || "",
          flatNo: flatNo || "",
          city: city || "",
          state: state || "",
          pincode: pincode || "",
        });
        if (value.length >= 3) {
          trackFieldCompletion("name", true);
        }
        break;

      case "school":
        if (individualReg) {
          updatedError.schoolName =
            value.length < 3 ? "Please enter a valid school name." : "";
        }
        setSchool(value);
        sessionStorage.setItem("school", value);
        setUserDetails({ ...userDetails, school: value });
        if (value.length >= 3) {
          trackFieldCompletion("school", true);
        }
        break;

      case "grade":
        updatedError.grade =
          value < 5 || value > 12
            ? "Please enter a valid grade between 5 - 12."
            : "";
        setGrade(Number(value));
        sessionStorage.setItem("grade", value);
        setUserDetails({ ...userDetails, grade: Number(value) });
        if (value >= 5 && value <= 12) {
          trackFieldCompletion("grade", true);
        }
        break;

      case "mobileNumber":
        const isValidMobile = backendSchool.isInternational
          ? dubaiMobileValidation.test(value)
          : mobileValidation.test(value);
        updatedError.mobileNumber = isValidMobile
          ? ""
          : "Please enter a valid mobile number.";
        setUserDetails({ ...userDetails, mobileNumber: value });
        sessionStorage.setItem("mobileNumber", value);
        if (isValidMobile) {
          trackFieldCompletion("mobile", true);
        }
        break;

      case "email":
        updatedError.email = emailValidation.test(value)
          ? ""
          : "Please enter a valid email.";
        setEmail(value);
        sessionStorage.setItem("email", value);
        setUserDetails({ ...userDetails, email: value });
        if (emailValidation.test(value)) {
          trackFieldCompletion("email", true);
        }
        break;

      default:
        break;
    }

    setError(updatedError);
  };

  const [loading, setLoading] = useState(false);

  // Form change handler
  const handleFormChangeAddress = (event: any) => {
    const { value, name } = event.target;

    // Only allow numeric input for pincode
    if (name === "pincode" && /\D/.test(value)) {
      return; // Exit if non-numeric input is detected
    }

    let updatedError = { ...error };
    switch (name) {
      case "address":
        updatedError.address =
          value.length < 5
            ? "Please enter a valid address."
            : value.length > 80
            ? "Address cannot exceed 80 characters."
            : "";
        setAddress(value);
        sessionStorage.setItem("address", value);
        setUserDetails({ ...userDetails, address: value });
        if (value.length >= 5 && value.length <= 80) {
          trackFieldCompletion("address", true);
        }
        break;

      case "flatNo":
        updatedError.flatNo =
          value.length < 1 ? "Please enter a valid flat/house number." : "";
        setFlatNo(value);
        sessionStorage.setItem("flatNo", value);
        setUserDetails({ ...userDetails, flatNo: value });
        if (value.length >= 1) {
          trackFieldCompletion("flatNo", true);
        }
        break;

      case "city":
        updatedError.city =
          value.length < 2 ? "Please enter a valid city." : "";
        setCity(value);
        sessionStorage.setItem("city", value);
        setUserDetails({ ...userDetails, city: value });
        if (value.length >= 2) {
          trackFieldCompletion("city", true);
        }
        break;

      case "state":
        updatedError.state =
          value.length < 2 ? "Please select a valid state." : "";
        setState(value);
        sessionStorage.setItem("state", value);
        setUserDetails({ ...userDetails, state: value });
        if (value.length >= 2) {
          trackFieldCompletion("state", true);
        }
        break;

      case "pincode":
        updatedError.pincode =
          value.length !== 6 ? "Please enter a valid pincode." : "";
        setPincode(value);
        sessionStorage.setItem("pincode", value);
        setUserDetails({ ...userDetails, pincode: value });
        if (value.length === 6) {
          fetchStateAndCity(value);
        }
        break;

      default:
        break;
    }

    setError(updatedError);
  };

  return (
    <div className="parent-wrapper">
      <div className="child-wrapper">
        <FormInput
          loading={isLoading}
          isVisible={true}
          disabled={isOtpVerified} // Re-enabled OTP verification
          onBlur={(event: any) => {
            const { value } = event?.target;
            if (!mobileValidation.test(value)) {
              setError({
                ...error,
                mobileNumber: "Please enter a valid mobile number.",
              });
            } else {
              setError({ ...error, mobileNumber: "" });
            }
          }}
          title="Mobile Number *"
          value={userDetails.mobileNumber}
          onChange={(event) => {
            const { value } = event.target;
            const isValidMobile = backendSchool.isInternational
              ? dubaiMobileValidation.test(value)
              : mobileValidation.test(value);
            setError((prev) => ({
              ...prev,
              mobileNumber: isValidMobile
                ? ""
                : "Please enter a valid mobile number.",
            }));
            setUserDetails((prev) => ({ ...prev, mobileNumber: value }));
            sessionStorage.setItem("mobileNumber", value);

            if (isValidMobile) {
              trackFieldCompletion("mobile", true);
            }
          }}
          name="mobileNumber"
          type="name"
          onShowVerified={isOtpVerified} // Re-enabled OTP verification display
          error={error.mobileNumber}
        />

        {error.sendOtpError && (
          <div style={{ marginBottom: "10px" }} className="error-message">
            {error.sendOtpError}
          </div>
        )}
        {!error.mobileNumber && userDetails.mobileNumber && !isOtpVerified && (
          <button
            className="otp-button"
            onClick={handleOtp}
            disabled={isOtpFieldVisible && !isResendEnabled}
          >
            {isOtpFieldVisible
              ? `Resend OTP ${isResendEnabled ? "" : `(${resendTimer}s)`}`
              : "Verify"}
          </button>
        )}
        {isOtpFieldVisible && (
          <>
            <FormInput
              isVisible={true}
              loading={isLoading}
              disabled={false}
              onBlur={() => {}}
              title="Enter OTP"
              value={otp}
              onChange={(e: any) => {
                const value = e.target.value;
                setOtp(value);
                setIsOtpValid(otpValidation.test(value)); // Validate OTP
              }}
              name="otp"
              type="text"
              error={error.otp}
            />
            <button
              className="otp-button"
              onClick={verifyOtp}
              disabled={!isOtpValid}
            >
              Verify OTP
            </button>
          </>
        )}

        <FormInput
          isVisible={true}
          disabled={false}
          onBlur={(event: any) => {
            const { value, name } = event?.target;
            if (value.length <= 0) {
              setError({ ...error, name: "Please enter a valid name." });
            } else {
              setError({ ...error, name: "" });
              setUserDetails({ ...userDetails, name: value });
            }
          }}
          title="Student Name *"
          value={name}
          onChange={handleFormChange}
          name="name"
          type="name"
          error={error.name}
        />
        <FormInput
          isVisible={true}
          disabled={!individualReg}
          onBlur={(event: any) => {
            const { value, name } = event?.target;

            if (value.length <= 0) {
              setError({
                ...error,
                schoolName: "Please enter a valid school name.",
              });
            } else {
              setError({ ...error, schoolName: "" });
              setUserDetails({ ...userDetails, school: value });
            }
          }}
          title={individualReg ? "School Name *" : "School"}
          value={school}
          onChange={handleFormChange}
          name="school"
          type="name"
          error={error.schoolName}
        />
        <FormInput
          isVisible={!individualReg}
          disabled={true}
          onBlur={(event: any) => {
            const { value, name } = event?.target;

            if (value.length <= 0) {
              setError({
                ...error,
                schoolCity: "Please enter a valid school city.",
              });
            } else {
              setError({ ...error, schoolCity: "" });
              setUserDetails({ ...userDetails, school: value });
            }
          }}
          title="School City"
          value={backendSchool.city}
          onChange={() => {}}
          name="school"
          type="name"
          error={error.schoolCity}
        />
        <FormDropdown
          error={error.grade}
          setIsVisible={(value: boolean) => {
            setIsVisible(value);
            console.log(value);
          }}
          isVisible={isVisible}
          title="Grade *"
          value={grade}
          setValue={(value: number) => {
            if (value < 5 || value > 12) {
              setError({
                ...error,
                grade: "Please enter a valid grade between 5 - 12.",
              });
            } else {
              setError({ ...error, grade: "" });
              setGrade(value);
              sessionStorage.setItem("grade", value.toString());
              setUserDetails({ ...userDetails, grade: value });
              if (value >= 5 && value <= 12) {
                trackFieldCompletion("grade", true);
              }
            }
          }}
        />
        <div className="form-description">
          Enter the applicable grade for academic year 2025-26
        </div>
        <FormInput
          isVisible={true}
          disabled={false}
          onBlur={(event: any) => {
            const { value, name } = event?.target;

            if (value.length <= 0) {
              setError({ ...error, email: "Please enter a valid email." });
            } else {
              setError({ ...error, email: "" });
              setUserDetails({ ...userDetails, email: email });
            }
          }}
          title="Email *"
          value={email}
          onChange={handleFormChange}
          name="email"
          type="name"
          error={error.email}
        />

        <FormInput
          loading={loading}
          isVisible={true}
          disabled={!individualReg}
          onBlur={(event: any) => {
            const { value } = event?.target;
            if (value.length !== 6) {
              setError({ ...error, pincode: "Please enter a valid pincode." });
            } else {
              setError({ ...error, pincode: "" });
              setUserDetails({ ...userDetails, pincode: value });
            }
          }}
          title="Pincode *"
          value={pincode}
          onChange={handleFormChangeAddress}
          name="pincode"
          type="text"
          error={error.pincode}
        />

        <FormInput
          isVisible={true}
          disabled={!individualReg}
          onBlur={(event: any) => {
            const { value } = event?.target;
            if (value.length <= 0) {
              setError({ ...error, city: "Please enter a valid city." });
            } else {
              setError({ ...error, city: "" });
              setUserDetails({ ...userDetails, city: value });
            }
          }}
          title="City *"
          value={city}
          onChange={handleFormChangeAddress}
          name="city"
          type="text"
          error={error.city}
        />

        <StateDropdown
          value={state}
          onInputChange={(value: string) => {
            setState(value);
            sessionStorage.setItem("state", value);
            setUserDetails({ ...userDetails, state: value });
          }}
          isVisible={true}
          isDisabled={!individualReg}
        />
      </div>
    </div>
  );
};

export default FormCluster;
